import { createMasonryGrid } from './masonry-grid.js';
import Swiper from 'swiper';
import { FreeMode, Mousewheel } from 'swiper/modules';
import 'swiper/css';

const ajaxLoad = async ({ action, offset, target, button, onAppend }) => {
    if (!button || button.disabled || !target) return;
    const config = window.LED_THEME || {};
    const data = new FormData();
    data.append('action', action);
    data.append('offset', String(offset));
    data.append('nonce', config.homeNonce || '');

    const original = button.textContent;
    button.disabled = true;
    button.textContent = button.dataset.loadingLabel || 'Loading…';

    try {
        const response = await fetch(config.ajaxUrl, { method: 'POST', credentials: 'same-origin', body: data });
        const json = await response.json();
        if (!json?.success) throw new Error('Load failed');
        const wrapper = document.createElement('div');
        wrapper.innerHTML = json.data.html || '';
        const nodes = [...wrapper.children];
        nodes.forEach((node) => target.appendChild(node));
        onAppend?.(json.data, nodes);
        if (!json.data.hasMore) button.hidden = true;
    } catch (error) {
        button.textContent = button.dataset.errorLabel || 'Try Again';
        window.setTimeout(() => { button.textContent = original; }, 1800);
    } finally {
        button.disabled = false;
        if (!button.hidden && button.textContent !== (button.dataset.errorLabel || 'Try Again')) button.textContent = original;
    }
};

export function initHomePage({ gsap = null, ScrollTrigger = null, reducedMotion = false } = {}) {
    const root = document.querySelector('[data-home-page]');
    if (!root) return;

    const services = root.querySelector('[data-home-services]');
    if (services) {
        new Swiper(services, {
            modules: [FreeMode, Mousewheel],
            slidesPerView: 'auto',
            spaceBetween: 40,
            freeMode: { enabled: true, momentum: true, sticky: false },
            mousewheel: { forceToAxis: true, releaseOnEdges: true },
            grabCursor: true,
            speed: 650,
        });
    }

    const projectGrid = root.querySelector('[data-home-project-grid]');
    const projectLoad = root.querySelector('[data-home-project-load]');
    let projectOffset = Number(projectLoad?.dataset.offset || 4);
    const projectMasonry = createMasonryGrid(projectGrid, { verticalGap: 72 });

    const setupHomeProjectReveals = (cards) => {
        if (!gsap || !ScrollTrigger || reducedMotion || !cards?.length) return;
        cards.forEach((card) => {
            if (card.dataset.homeRevealReady === 'true') return;
            card.dataset.homeRevealReady = 'true';
            gsap.fromTo(
                card,
                { autoAlpha: 0.42, y: 16 },
                {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.72,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 94%',
                        once: true,
                    },
                },
            );
        });
    };

    setupHomeProjectReveals(projectGrid ? [...projectGrid.querySelectorAll('[data-project-card]')] : []);

    projectLoad?.addEventListener('click', () => ajaxLoad({
        action: 'led_home_projects',
        offset: projectOffset,
        target: projectGrid,
        button: projectLoad,
        onAppend: (data, nodes) => {
            projectOffset = Number(data.nextOffset || projectOffset);
            projectLoad.dataset.offset = String(projectOffset);
            projectMasonry?.refresh();
            setupHomeProjectReveals(nodes);
            ScrollTrigger?.refresh?.();
        },
    }));

    const journalGrid = root.querySelector('[data-home-journal-grid]');
    const journalLoad = root.querySelector('[data-home-journal-load]');
    let journalOffset = Number(journalLoad?.dataset.offset || 2);
    journalLoad?.addEventListener('click', () => ajaxLoad({
        action: 'led_home_journal',
        offset: journalOffset,
        target: journalGrid,
        button: journalLoad,
        onAppend: (data, nodes) => {
            journalOffset = Number(data.nextOffset || journalOffset);
            journalLoad.dataset.offset = String(journalOffset);
            if (gsap && !reducedMotion && nodes.length) {
                gsap.from(nodes, { opacity: 0, y: 24, duration: 0.75, stagger: 0.08, ease: 'power3.out' });
            }
            ScrollTrigger?.refresh?.();
        },
    }));
}
